import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocalStorageItem, errorToast, successToast } from '../../../utils/helper';
import { CompanyApi } from '../../../api/companyApi';
import { DEPARTMENT } from '../../../utils/constants';
import Autocomplete from '../../../component/common/Autocomplete';
import AudioPlayer from '../../../component/AudioPlayer/AudioPlayer';

// Import recognition icons
import ThankYouIcon from './icons/High-Five--Streamline-Flex 1.svg';
import GreatWorkIcon from './icons/Trophy--Streamline-Flex 1.png';
import TeamworkIcon from './icons/User-Multiple-Group--Streamline-Flex 2.svg';
import LeadershipIcon from './icons/Location-Compass-2--Streamline-Flex 1.svg';
import InnovationIcon from './icons/Flash-1--Streamline-Flex 1.svg';
import WellbeingSupportIcon from './icons/Sprout--Streamline-Flex 1.svg';
import KindnessIcon from './icons/Happy-Face--Streamline-Flex 1.svg';
import ResilienceIcon from './icons/Shield-Heart-Care-Protection--Streamline-Flex 1.svg';
import ProblemSolvingIcon from './icons/Module-Puzzle-1--Streamline-Flex 1.svg';
import SteppingUpIcon from './icons/Rocket--Streamline-Flex 1.svg';
import EmotionalSupportIcon from './icons/Happy-Sad-Faces--Streamline-Flex 1.svg';
import ListeningIcon from './icons/Ear-Hearing--Streamline-Flex 1.svg';

/**
 * Send Shoutout Component
 * Form to send employee recognition
 * @returns {JSX.Element} SendShoutout component
 */
const SendShoutout = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    recognitionType: '',
    recipientType: 'individual', // individual or team
    recipientId: '',
    message: '',
    visibility: 'public' // public or private
  });
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Ref to track if a fetch is in progress and abort controller
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';
  const companyId = userData?.companyId;

  const recognitionTypes = [
    { value: 'thank_you', label: 'Thank You', icon: ThankYouIcon },
    { value: 'great_work', label: 'Great Work', icon: GreatWorkIcon },
    { value: 'teamwork', label: 'Teamwork', icon: TeamworkIcon },
    { value: 'leadership', label: 'Leadership', icon: LeadershipIcon },
    { value: 'innovation', label: 'Innovation', icon: InnovationIcon },
    { value: 'wellbeing_support', label: 'Wellbeing Support', icon: WellbeingSupportIcon },
    { value: 'kindness', label: 'Kindness', icon: KindnessIcon },
    { value: 'resilience', label: 'Resilience', icon: ResilienceIcon },
    { value: 'problem_solving', label: 'Problem Solving', icon: ProblemSolvingIcon },
    { value: 'stepping_up', label: 'Stepping Up', icon: SteppingUpIcon },
    { value: 'emotional_support', label: 'Emotional Support', icon: EmotionalSupportIcon },
    { value: 'listening', label: 'Listening', icon: ListeningIcon }
  ];

  // Fetch employees with search on component mount
  useEffect(() => {
    if (companyId && formData.recipientType === 'individual') {
      // Only fetch initial data once when switching to individual type
      return;
    }
  }, [companyId, formData.recipientType]);

  // Fetch departments on component mount
  useEffect(() => {
    if (companyId && formData.recipientType === 'team' && departmentList.length === 0) {
      fetchDepartments();
    }
  }, [companyId, formData.recipientType]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchEmployees = useCallback(async (searchKey = '') => {
    // Prevent multiple simultaneous requests
    if (isFetchingRef.current) {
      console.log('Request already in progress, skipping...');
      return;
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      console.log('Aborting previous request...');
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      isFetchingRef.current = true;
      setLoadingData(true);
      setApiError(null);

      console.log('Fetching employees with searchKey:', searchKey);
      const response = await CompanyApi.getCompanyUser(1, 50, searchKey, 'name', 1, {});

      if (response?.data?.meta?.code === 1) {
        setEmployeeList(response?.data?.data || []);
        setApiError(null);
      } else if (response?.data?.meta?.code === 0) {
        setEmployeeList([]);
        setApiError(response?.data?.meta?.message || 'Failed to fetch employees');
      } else {
        setEmployeeList([]);
        setApiError('Unexpected response from server');
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Error fetching employees:', error);
        setEmployeeList([]);
        setApiError(error.message || 'Failed to fetch employees. Please try again.');
      } else {
        console.log('Request was cancelled');
      }
    } finally {
      isFetchingRef.current = false;
      setLoadingData(false);
      abortControllerRef.current = null;
    }
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoadingData(true);
      setApiError(null);

      const response = await CompanyApi.getCompanyDepartmentList(companyId);

      if (response.data?.meta?.code === 1) {
        const list = response.data?.data.map((obj) => ({
          ...obj,
          name: obj.department_name,
          value: obj.department_name
        }));

        const combined = [...list, ...DEPARTMENT].filter((item) => item.name !== 'Select');

        const sortedList = combined.sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });

        setDepartmentList(sortedList);
        setApiError(null);
      } else if (response.data?.meta?.code === 0) {
        setDepartmentList([]);
        setApiError(response.data?.meta?.message || 'Failed to fetch departments');
      } else {
        setDepartmentList([]);
        setApiError('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartmentList([]);
      setApiError(error.message || 'Failed to fetch departments. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Reset recipient when type changes
    if (field === 'recipientType') {
      setFormData((prev) => ({ ...prev, recipientId: '' }));
      setSelectedRecipient(null);
      setEmployeeList([]);
      setApiError(null);

      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isFetchingRef.current = false;
    }
  };

  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
    if (formData.recipientType === 'individual') {
      setFormData((prev) => ({ ...prev, recipientId: recipient._id }));
    } else {
      setFormData((prev) => ({ ...prev, recipientId: recipient.value || recipient.name }));
    }
  };

  const handleEmployeeSearch = useCallback(
    (searchKey) => {
      setApiError(null);
      fetchEmployees(searchKey);
    },
    [fetchEmployees]
  );

  const handleDepartmentSearch = useCallback(
    (searchKey) => {
      setApiError(null);
      // Departments are pre-loaded, just filter locally
      if (searchKey) {
        const allDepts = [...departmentList];
        const filtered = allDepts.filter((dept) =>
          dept.name.toLowerCase().includes(searchKey.toLowerCase())
        );
        setDepartmentList(filtered);
      } else {
        // Reload all departments if search is cleared
        if (formData.recipientType === 'team' && departmentList.length === 0) {
          fetchDepartments();
        }
      }
    },
    [departmentList, formData.recipientType]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Show confirmation if no message
    if (!formData.message.trim()) {
      setShowConfirmation(true);
      return;
    }

    await sendShoutout();
  };

  const sendShoutout = async () => {
    setLoading(true);
    try {
      // Map recognition type to uppercase for API
      const behaviourMap = {
        thank_you: 'THANK_YOU',
        great_work: 'GREAT_WORK',
        teamwork: 'TEAMWORK',
        leadership: 'LEADERSHIP',
        innovation: 'INNOVATION',
        wellbeing_support: 'WELLBEING_SUPPORT',
        kindness: 'KINDNESS',
        resilience: 'RESILIENCE',
        problem_solving: 'PROBLEM_SOLVING',
        stepping_up: 'STEPPING_UP',
        emotional_support: 'EMOTIONAL_SUPPORT',
        listening: 'LISTENING'
      };

      // Build request payload
      const formPayload = new FormData();
      formPayload.append('behaviour', behaviourMap[formData.recognitionType]);
      formPayload.append('isPrivate', formData.visibility === 'private');
      if (formData.message) {
        formPayload.append('message', formData.message);
      }

      // Add recipient based on type
      if (formData.recipientType === 'individual') {
        formPayload.append('userId', formData.recipientId);
      } else {
        formPayload.append('department', formData.recipientId);
      }

      if (imageFile) {
        formPayload.append('image', imageFile);
      }
      if (audioFile) {
        formPayload.append('audio', audioFile);
      }

      const response = await CompanyApi.sendShoutout(formPayload);

      if (response?.data?.meta?.code === 1) {
        successToast('Shoutout sent successfully!');
        navigate(`/${slug}/shoutouts`);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to send shoutout');
      }
    } catch (error) {
      console.error('Error sending shoutout:', error);
      errorToast(
        error?.response?.data?.meta?.message ||
          error?.message ||
          'Failed to send shoutout. Please try again.'
      );
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleConfirmSend = () => {
    sendShoutout();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/${slug}/shoutouts`)}
          className="text-shoorah-primary hover:text-shoorah-primary-dark mb-4 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Recognitions
        </button>
        <h1 className="text-2xl font-bold text-shoorah-lightBlack dark:text-shoorah-darkBg">
          Send Recognition
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Acknowledge and celebrate your team member's contribution
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
        {/* Recognition Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Recognition Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {recognitionTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleInputChange('recognitionType', type.value)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.recognitionType === type.value
                    ? 'border-shoorah-primary bg-shoorah-primary/10'
                    : 'border-gray-200 dark:border-shoorah-darkBorder hover:border-shoorah-primary/50'
                }`}
              >
                <div className="mb-2 flex justify-center">
                  <img src={type.icon} alt={type.label} className="w-8 h-8" />
                </div>
                <div className="text-sm font-medium">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Recipient Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Recipient Type <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => handleInputChange('recipientType', 'individual')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                formData.recipientType === 'individual'
                  ? 'border-shoorah-primary bg-shoorah-primary/10'
                  : 'border-gray-200 dark:border-shoorah-darkBorder'
              }`}
            >
              Individual
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('recipientType', 'team')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                formData.recipientType === 'team'
                  ? 'border-shoorah-primary bg-shoorah-primary/10'
                  : 'border-gray-200 dark:border-shoorah-darkBorder'
              }`}
            >
              Team
            </button>
          </div>
        </div>

        {/* Recipient Selection */}
        <div className="mb-6">
          {formData.recipientType === 'individual' ? (
            <Autocomplete
              label="Select Employee"
              placeholder="Search by name or email..."
              options={employeeList}
              onSearch={handleEmployeeSearch}
              onSelect={handleRecipientSelect}
              value={formData.recipientId}
              displayValue={selectedRecipient?.name || ''}
              loading={loadingData}
              required={true}
              error={apiError || ''}
              displayKey="name"
              valueKey="_id"
            />
          ) : (
            <Autocomplete
              label="Select Team"
              placeholder="Search department..."
              options={departmentList}
              onSearch={handleDepartmentSearch}
              onSelect={handleRecipientSelect}
              value={formData.recipientId}
              displayValue={selectedRecipient?.name || ''}
              loading={loadingData}
              required={true}
              error={apiError || ''}
              displayKey="name"
              valueKey="value"
            />
          )}
        </div>

        {/* Message */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Custom Message <span className="text-gray-500">(Optional)</span>
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={4}
            placeholder="Add a personal message to accompany this recognition..."
            className="w-full p-3 border border-gray-300 dark:border-shoorah-darkBorder rounded-lg focus:ring-2 focus:ring-shoorah-primary dark:bg-gray-700"
          />
        </div>

        {/* Attachments */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Image <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  setImagePreview(URL.createObjectURL(file));
                }
              }}
            />
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-shoorah-darkBorder"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    if (imageInputRef.current) imageInputRef.current.value = '';
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-gray-300 dark:border-shoorah-darkBorder rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-shoorah-primary hover:text-shoorah-primary transition-colors"
              >
                <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm">Click to upload image</span>
              </button>
            )}
          </div>

          {/* Audio Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Audio <span className="text-gray-500">(Optional, MP3)</span>
            </label>
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/mp3,audio/mpeg"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setAudioFile(file);
                  setAudioPlaying(false);
                  if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
                  setAudioPreviewUrl(URL.createObjectURL(file));
                }
              }}
            />
            {audioFile ? (
              <div className="w-full border border-gray-300 dark:border-shoorah-darkBorder rounded-lg p-3">
                <AudioPlayer
                  audioPath={audioPreviewUrl}
                  playing={audioPlaying}
                  onPlay={() => setAudioPlaying(true)}
                  pauseHandler={() => setAudioPlaying(false)}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {audioFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAudioPlaying(false);
                      setAudioFile(null);
                      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
                      setAudioPreviewUrl(null);
                      if (audioInputRef.current) audioInputRef.current.value = '';
                    }}
                    className="text-xs text-red-500 hover:text-red-600 ml-2 shrink-0"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => audioInputRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-gray-300 dark:border-shoorah-darkBorder rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-shoorah-primary hover:text-shoorah-primary transition-colors"
              >
                <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
                <span className="text-sm">Click to upload MP3</span>
              </button>
            )}
          </div>
        </div>

        {/* Visibility */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Visibility <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => handleInputChange('visibility', 'public')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                formData.visibility === 'public'
                  ? 'border-shoorah-primary bg-shoorah-primary/10'
                  : 'border-gray-200 dark:border-shoorah-darkBorder'
              }`}
            >
              <div className="font-medium">Public</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Visible to all employees
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('visibility', 'private')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                formData.visibility === 'private'
                  ? 'border-shoorah-primary bg-shoorah-primary/10'
                  : 'border-gray-200 dark:border-shoorah-darkBorder'
              }`}
            >
              <div className="font-medium">Private</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Visible to recipient only
              </div>
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/${slug}/shoutouts`)}
            className="flex-1 px-6 py-3 border border-gray-300 dark:border-shoorah-darkBorder text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.recognitionType || !formData.recipientId || loading}
            className="btn-primary flex-1"
          >
            {loading ? 'Sending...' : 'Send Recognition'}
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              Send without message?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You haven't added a custom message. Are you sure you want to send this recognition
              without a personal note?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleCancelConfirmation}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-shoorah-darkBorder text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSend}
                className="btn-primary flex-1"
              >
                Send Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendShoutout;
